import React, { useEffect, useState } from "react";
import DecorativeSquares from "../../components/ui/DecorativeSquares";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TextInput } from "../../components/form/inputs";
import UploadRow from "../../components/upload/UploadRow";
import uploadIcon from "../../assets/images/svgs/other/uploadImg.svg";
import InputBase from "../../components/form/inputs/InputBase";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import Card from "../../components/Cards/Card";
import { useDispatch, useSelector } from "react-redux";
import {
  addQuery,
  getAllFaqs,
  getPastquery,
} from "../../redux/thunks/faqThunk";
import { uploadPhoto } from "../../redux/thunks/registerThunks";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";

const Supports = () => {
  const dispatch = useDispatch();
  const { faqList, isFaqLoading, pastQueries } = useSelector(
    (state) => state.faqs,
  );
  const [qTitle, setQTitle] = useState("");
  const [qQuestion, setQQuestion] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    dispatch(getAllFaqs());
  }, [dispatch]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("faqs");
  const [openIndex, setOpenIndex] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [isSupportLoading, setIsSupportLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const handlepayupload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file); // 🔥 IMPORTANT
    setPreviewImg(URL.createObjectURL(file));
    e.target.value = null;
  };

  const handleRemoveImage = () => {
    if (previewImg) {
      URL.revokeObjectURL(previewImg);
      setPreviewImg(null);
    }
  };

  const handleQueryValidate = () => {
    const newErrors = {};
    if (!qTitle) {
      newErrors.qTitle = "Please enter a title";
    }
    if (!qQuestion) {
      newErrors.qQuestion = "Please enter your query";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSendQuery = async () => {
    if (!handleQueryValidate()) return;

    try {
      setIsSupportLoading(true); // show shimmer while submitting

      let uploadedFileName = null;

      // ✅ image upload
      if (selectedFile) {
        const formData = new FormData();
        formData.append("type", "support_queries");
        formData.append("image", selectedFile);

        const uploadRes = await dispatch(uploadPhoto(formData)).unwrap();
        uploadedFileName = uploadRes?.data?.temp_image_name || null;
      }

      const payload = {
        title: qTitle,
        query: qQuestion,
        image: uploadedFileName,
      };

      const res = await dispatch(addQuery(payload)).unwrap();

      toast.success(res.message || "Query submitted successfully");

      // ✅ reset form
      setQTitle("");
      setQQuestion("");
      setPreviewImg(null);
      setSelectedFile(null);

      // ✅ reload past queries
      await dispatch(getPastquery({ per_page: 10 }));
    } catch (err) {
      console.error("Query error:", err);
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsSupportLoading(false); // stop shimmer
    }
  };

  useEffect(() => {
    dispatch(getPastquery({ per_page: 10 }));
  }, [dispatch]);

  return (
    <section className="relative max-w-7xl bg-transparent md:bg-white rounded-md mx-auto px-2 md:px-4 py-6 pb-28">
      <DecorativeSquares />

      {/* ================= HEADER ================= */}
      <div className="sticky top-2 z-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-md px-4 py-3 flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/profile")}
          className="p-2 rounded-full hover:bg-gray-100 transition"
        >
          <FaArrowLeft size={20} />
        </button>

        <h1 className="text-lg font-semibold text-gray-900">
          Support & Queries
        </h1>

        <div className="w-10" />
      </div>

      {/* ================= TABS ================= */}
      <div className="max-w-7xl bg-transparent md:bg-white p-0 md:p-4 shadow-sm rounded-3xl border border-gray-100">
        <div className="relative mb-6">
          {/* Mobile pill container */}
          <div className="relative flex p-1 bg-gray-100 rounded-full md:bg-transparent md:p-0 md:rounded-none md:border-b md:border-gray-200">
            {/* Sliding mobile background */}
            <div
              className="absolute top-1 bottom-1 w-1/2 bg-[#FF6943] rounded-full transition-all duration-300 ease-in-out md:hidden"
              style={{
                left: activeTab === "faqs" ? "4px" : "50%",
              }}
            />

            <button
              onClick={() => setActiveTab("faqs")}
              className={`relative flex-1 py-2 md:py-3 text-sm font-medium transition-colors duration-300
      ${
        activeTab === "faqs"
          ? "text-white md:text-[#FF6943]"
          : "text-gray-500 hover:text-[#FF6943]"
      }`}
            >
              FAQs
            </button>

            <button
              onClick={() => setActiveTab("queries")}
              className={`relative flex-1 py-2 md:py-3 text-sm font-medium transition-colors duration-300
      ${
        activeTab === "queries"
          ? "text-white md:text-[#FF6943]"
          : "text-gray-500 hover:text-[#FF6943]"
      }`}
            >
              My Queries
            </button>
          </div>

          {/* Desktop sliding underline */}
          <div
            className="hidden md:block absolute bottom-0 h-[2px] bg-[#FF6943] rounded-full transition-all duration-300 ease-in-out"
            style={{
              width: "50%",
              left: activeTab === "faqs" ? "0%" : "50%",
            }}
          />
        </div>

        {/* ================= CONTENT ================= */}
        {activeTab === "faqs" && (
          <div className="space-y-3">
            {isFaqLoading ? (
              // Shimmer placeholders while loading
              Array(3)
                .fill(0)
                .map((_, index) => (
                  <div
                    key={index}
                    className="bg-transparent md:bg-white rounded-2xl px-4 py-4 shadow-sm"
                  >
                    {/* Question placeholder */}
                    <div className="h-4 shimmer rounded w-3/4 mb-3"></div>
                    {/* Chevron placeholder */}
                    <div className="h-4 shimmer rounded w-6"></div>
                    {/* Answer placeholder */}
                    <div className="mt-3 space-y-2">
                      <div className="h-3 shimmer rounded w-full"></div>
                      <div className="h-3 shimmer rounded w-5/6"></div>
                    </div>
                  </div>
                ))
            ) : faqList && faqList.length > 0 ? (
              // Render actual FAQs
              faqList.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-transparent md:bg-white rounded-2xl px-4 py-4 shadow-sm"
                >
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="font-medium text-gray-900 text-sm">
                      {item.question}
                    </span>

                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openIndex === index ? "max-h-40 mt-3" : "max-h-0"
                    }`}
                  >
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.answers}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">
                No FAQs available
              </p>
            )}
          </div>
        )}

        {activeTab === "queries" && (
          <>
            <div className="bg-white rounded-2xl p-4 md:p-6 text-start space-y-6 text-sm text-gray-500 shadow-sm">
              {isSupportLoading ? (
                // Shimmer placeholders
                <>
                  <div className="space-y-4">
                    <div className="h-5 shimmer rounded w-1/3"></div>{" "}
                    {/* Title */}
                    <div className="h-20 shimmer rounded w-full"></div>{" "}
                    {/* Query textarea */}
                    <div className="h-6 shimmer rounded w-1/2"></div>{" "}
                    {/* Upload heading */}
                    <div className="h-32 shimmer rounded w-28"></div>{" "}
                    {/* Image preview */}
                    <div className="h-10 shimmer rounded w-1/3 mt-2"></div>{" "}
                    {/* Submit button */}
                    <div className="h-5 shimmer rounded w-1/4 mt-6"></div>{" "}
                    {/* Past queries heading */}
                    <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-2 mt-4">
                      {/* Past Queries placeholders */}
                      {Array(2)
                        .fill(0)
                        .map((_, idx) => (
                          <div
                            key={idx}
                            className="h-24 shimmer rounded w-full"
                          ></div>
                        ))}
                    </div>
                  </div>
                </>
              ) : (
                // Actual content
                <>
                  <div>
                    <TextInput
                      label="Title"
                      required={true}
                      placeholder="Enter Title"
                      name="title"
                      value={qTitle}
                      onChange={(e) => setQTitle(e.target.value)}
                    />
                    {errors.qTitle && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.qTitle}
                      </p>
                    )}
                  </div>

                  <div>
                    <InputBase
                      type="textarea"
                      required={true}
                      rows={2}
                      label={"Add Query"}
                      placeholder="Enter Query"
                      name="question"
                      value={qQuestion}
                      onChange={(e) => setQQuestion(e.target.value)}
                    />
                    {errors.qQuestion && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.qQuestion}
                      </p>
                    )}
                  </div>

                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Upload Payment Proof
                    </h2>
                    <div className="w-full mt-2">
                      <UploadRow
                        id="payment-proof-upload"
                        icon={uploadIcon}
                        onChange={handlepayupload}
                      />
                    </div>

                    {previewImg && (
                      <div className="mt-4 relative w-28 h-32">
                        <img
                          src={previewImg}
                          alt="Payment Proof"
                          className="w-full h-full object-cover border border-gray-200 rounded-xl"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black transition"
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  <PrimaryBtn
                    btnName="Submit Query"
                    clickEvent={handleSendQuery}
                    isLoading={isFaqLoading}
                  />

                  <div>
                    <h3 className="text-start text-[#191919] font-medium text-base">
                      Past Queries
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 mt-4">
                      {pastQueries && pastQueries.length > 0 ? (
                        pastQueries.map((data) => (
                          <Card
                            key={data.id}
                            varient="queries"
                            title={data.title}
                            description={data.query}
                            date={`Created : ${data.created_at}`}
                            imagUp={data.image_url}
                            galImg={data.image_url}
                            status={data.status}
                          />
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 text-center">
                          No past queries found
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Supports;
